import { useMutation, useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';

import { queryClient } from '@lib/queryClient';
import { sharedQueryKeys } from '@services/queries';
import { CountryList, FilesUploadResponse, PromptGroups, RolesWithPrompts } from '@services/types';

import {
    deleteGeneralKnowledgeFile,
    deleteGeneralPredefinedPrompt,
    deleteGeneralPredefinedPromptGroup,
    deletePredefinedPromptByRole,
    getAllAgents,
    getAvatarSupportedLanguages,
    getFileUploadConstraints,
    getGeneralKnowledgeFiles,
    getSupportedCountriesForAdminPanel,
    updateAgent,
    updateAvatarSupportedLanguages,
    updateGeneralKnowledgeFiles,
    updateGeneralPredefinedPrompts,
    updatePredefinedPromptsByRole,
} from './api';
import {
    AgentConfig,
    AgentsConfig,
    AvatarSupportedLanguages,
    FilesDataToUpdate,
    FileUploadConstraints,
    GeneralKnowledgeFiles,
    GroupsWithPromptsRequest,
    RolesWithPromptsRequest,
} from './types';

const queryKeys = {
    allAgents: 'allAgents',
    generalKnowledgeFiles: 'generalKnowledgeFiles',
    fileUploadConstraints: 'fileUploadConstraints',
    avatarSupportedLanguages: 'avatarSupportedLanguages',
    supportedCountriesForAdminPanel: 'supportedCountriesForAdminPanel',
} as const;

export const useAllAgents = () =>
    useQuery<AgentsConfig, Error>({
        queryKey: [queryKeys.allAgents],
        queryFn: getAllAgents,
    });

export const useUpdateAgent = () =>
    useMutation<void, Error, AgentConfig>({
        mutationFn: updateAgent,
        onSuccess: (_, updatedAgent) => {
            queryClient.setQueryData<AgentsConfig>([queryKeys.allAgents], (oldData) =>
                oldData?.map((agent) => (agent.id === updatedAgent.id ? updatedAgent : agent))
            );
        },
    });

export const useGeneralKnowledgeFiles = () =>
    useQueries({
        queries: [
            {
                queryKey: [queryKeys.generalKnowledgeFiles],
                queryFn: getGeneralKnowledgeFiles,
            },
            {
                queryKey: [queryKeys.fileUploadConstraints],
                queryFn: getFileUploadConstraints,
            },
            {
                queryKey: [queryKeys.supportedCountriesForAdminPanel],
                queryFn: getSupportedCountriesForAdminPanel,
            },
        ],
        combine: (
            results: [
                UseQueryResult<GeneralKnowledgeFiles, Error>,
                UseQueryResult<FileUploadConstraints, Error>,
                UseQueryResult<CountryList, Error>,
            ]
        ) => {
            const [
                generalKnowledgeFilesResult,
                fileUploadConstraintsResult,
                supportedCountriesResult,
            ] = results;
            return {
                data: {
                    generalKnowledgeFiles: generalKnowledgeFilesResult.data,
                    fileUploadConstraints: fileUploadConstraintsResult.data,
                    countryList: supportedCountriesResult.data,
                },
                isLoading: results.some((r) => r.isLoading),
                isFetching: results.some((r) => r.isFetching),
                isError: results.some((r) => r.isError),
                error: results.find((r) => r.error)?.error ?? null,
            };
        },
    });

export const useUpdateGeneralKnowledgeFiles = () =>
    useMutation<FilesUploadResponse, Error, FilesDataToUpdate>({
        mutationFn: updateGeneralKnowledgeFiles,
        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: [queryKeys.generalKnowledgeFiles],
            });
        },
    });

export const useDeleteGeneralKnowledgeFile = () =>
    useMutation<void, Error, string>({
        mutationFn: deleteGeneralKnowledgeFile,
        onSuccess: (_, deletedFilePath) => {
            queryClient.setQueryData<GeneralKnowledgeFiles>(
                [queryKeys.generalKnowledgeFiles],
                (oldFiles) => {
                    if (!oldFiles) return oldFiles;
                    return oldFiles.map((countryFiles) => ({
                        ...countryFiles,
                        documents: countryFiles.documents.filter(
                            (file) => file.absolutePath !== deletedFilePath
                        ),
                    }));
                }
            );
        },
    });

export const useAvatarSupportedLanguages = () =>
    useQuery<AvatarSupportedLanguages, Error>({
        queryKey: [queryKeys.avatarSupportedLanguages],
        queryFn: getAvatarSupportedLanguages,
    });

export const useUpdateAvatarSupportedLanguages = () =>
    useMutation<void, Error, AvatarSupportedLanguages>({
        mutationFn: updateAvatarSupportedLanguages,
        onSuccess: (_, updatedLanguages) => {
            queryClient.setQueryData<AvatarSupportedLanguages>(
                [queryKeys.avatarSupportedLanguages],
                updatedLanguages
            );
            queryClient.refetchQueries({
                queryKey: [sharedQueryKeys.avatarSupportedLocales],
            });
        },
    });

export const useUpdatePredefinedPromptsByRole = () =>
    useMutation<void, Error, RolesWithPromptsRequest>({
        mutationFn: updatePredefinedPromptsByRole,
        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: [sharedQueryKeys.predefinedPromptsByRole],
            });
        },
    });

export const useDeletePredefinedPromptByRole = () =>
    useMutation<void, Error, { roleId: string; promptId: string }>({
        mutationFn: ({ roleId, promptId }) => deletePredefinedPromptByRole(roleId, promptId),
        onSuccess: (_, { roleId, promptId }) => {
            queryClient.setQueryData<RolesWithPrompts>(
                [sharedQueryKeys.predefinedPromptsByRole],
                (oldData) => {
                    if (!oldData) return oldData;

                    return oldData.map((role) => {
                        if (role.id !== roleId) return role;

                        return {
                            ...role,
                            prompts: role.prompts.filter((prompt) => prompt.promptId !== promptId),
                        };
                    });
                }
            );
        },
    });

export const useUpdateGeneralPredefinedPrompts = () =>
    useMutation<void, Error, GroupsWithPromptsRequest>({
        mutationFn: updateGeneralPredefinedPrompts,
        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: [sharedQueryKeys.generalPredefinedPrompts],
            });
        },
    });

export const useDeleteGeneralPredefinedPrompt = () =>
    useMutation<void, Error, { groupId: string; promptId: string }>({
        mutationFn: ({ groupId, promptId }) => deleteGeneralPredefinedPrompt(groupId, promptId),
        onSuccess: (_, { groupId, promptId }) => {
            queryClient.setQueryData<PromptGroups>(
                [sharedQueryKeys.generalPredefinedPrompts],
                (oldData) => {
                    if (!oldData) return oldData;

                    return oldData.map((group) => {
                        if (group.id !== groupId) return group;

                        return {
                            ...group,
                            prompts: group.prompts.filter((prompt) => prompt.promptId !== promptId),
                        };
                    });
                }
            );
        },
    });

export const useDeleteGeneralPredefinedPromptGroup = () =>
    useMutation<void, Error, string>({
        mutationFn: deleteGeneralPredefinedPromptGroup,
        onSuccess: (_, groupId) => {
            queryClient.setQueryData<PromptGroups>(
                [sharedQueryKeys.generalPredefinedPrompts],
                (oldData) => oldData?.filter((group) => group.id !== groupId)
            );
        },
    });
